import React, { useState, useEffect } from "react";
import { importacoes, fornecedores, produtos } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Eye, Ship, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate } from "../../lib/utils";

export function ImportacoesPage() {
  const [data, setData] = useState([]);
  const [fornecedoresList, setFornecedoresList] = useState([]);
  const [produtosList, setProdutosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    numero_processo: "",
    fornecedor_id: "",
    fornecedor_nome: "",
    data_pedido: new Date().toISOString().split("T")[0],
    data_prevista_embarque: "",
    data_prevista_chegada: "",
    valor_frete: 0,
    valor_seguro: 0,
    valor_impostos: 0,
    valor_desembaraco: 0,
    observacoes: "",
    itens: [],
  });

  const [newItem, setNewItem] = useState({
    produto_id: "",
    produto_descricao: "",
    quantidade: 1,
    custo_unitario: 0,
  });

  const fetchData = async () => {
    try {
      const [impsRes, fornecedoresRes, produtosRes] = await Promise.all([
        importacoes.list(),
        fornecedores.list(),
        produtos.list(),
      ]);
      setData(impsRes.data);
      setFornecedoresList(fornecedoresRes.data.filter((f) => f.tipo_fornecedor === "internacional"));
      setProdutosList(produtosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar importações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.itens.length === 0) {
      toast.error("Adicione pelo menos um item");
      return;
    }
    try {
      const valorTotalProdutos = formData.itens.reduce((acc, i) => acc + i.quantidade * i.custo_unitario, 0);
      if (editingItem) {
        await importacoes.update(editingItem.id, { ...formData, valor_total_produtos: valorTotalProdutos });
        toast.success("Importação atualizada!");
      } else {
        await importacoes.create({ ...formData, valor_total_produtos: valorTotalProdutos });
        toast.success("Importação criada!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      numero_processo: item.numero_processo || "",
      fornecedor_id: item.fornecedor_id || "",
      fornecedor_nome: item.fornecedor_nome || "",
      data_pedido: item.data_pedido || "",
      data_prevista_embarque: item.data_prevista_embarque || "",
      data_prevista_chegada: item.data_prevista_chegada || "",
      valor_frete: item.valor_frete || 0,
      valor_seguro: item.valor_seguro || 0,
      valor_impostos: item.valor_impostos || 0,
      valor_desembaraco: item.valor_desembaraco || 0,
      observacoes: item.observacoes || "",
      itens: item.itens || [],
    });
    setIsDialogOpen(true);
  };

  const handleFornecedorChange = (fornecedorId) => {
    const fornecedor = fornecedoresList.find((f) => f.id === fornecedorId);
    setFormData({
      ...formData,
      fornecedor_id: fornecedorId,
      fornecedor_nome: fornecedor?.razao_social || "",
    });
  };

  const handleProdutoChange = (produtoId) => {
    const produto = produtosList.find((p) => p.id === produtoId);
    setNewItem({
      ...newItem,
      produto_id: produtoId,
      produto_descricao: produto?.descricao || "",
      custo_unitario: produto?.custo_unitario_compra || 0,
    });
  };

  const addItem = () => {
    if (!newItem.produto_id || newItem.quantidade <= 0) {
      toast.error("Selecione um produto e quantidade válida");
      return;
    }
    setFormData({
      ...formData,
      itens: [...formData.itens, newItem],
    });
    setNewItem({
      produto_id: "",
      produto_descricao: "",
      quantidade: 1,
      custo_unitario: 0,
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      itens: formData.itens.filter((_, i) => i !== index),
    });
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await importacoes.update(id, { status_importacao: newStatus });
      toast.success("Status atualizado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      numero_processo: "",
      fornecedor_id: "",
      fornecedor_nome: "",
      data_pedido: new Date().toISOString().split("T")[0],
      data_prevista_embarque: "",
      data_prevista_chegada: "",
      valor_frete: 0,
      valor_seguro: 0,
      valor_impostos: 0,
      valor_desembaraco: 0,
      observacoes: "",
      itens: [],
    });
    setNewItem({
      produto_id: "",
      produto_descricao: "",
      quantidade: 1,
      custo_unitario: 0,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta importação?")) return;
    try {
      await importacoes.delete(id);
      toast.success("Importação excluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const filteredData = data.filter((item) => {
    const matchesSearch =
      item.numero_processo?.toLowerCase().includes(search.toLowerCase()) ||
      item.fornecedor_nome?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status_importacao === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const emAndamento = data.filter((i) => i.status_importacao === "em_andamento").length;

  const columns = [
    { key: "numero_processo", header: "Processo" },
    { key: "fornecedor_nome", header: "Fornecedor" },
    { key: "data_pedido", header: "Pedido", render: (v) => formatDate(v) },
    { key: "data_prevista_chegada", header: "Prev. Chegada", render: (v) => formatDate(v) },
    { key: "valor_total_produtos", header: "Valor Produtos", render: (v) => formatCurrency(v) },
    {
      key: "status_importacao",
      header: "Status",
      render: (v) => <StatusBadge status={v} />,
    },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setViewItem(row)} data-testid={`view-importacao-${row.id}`}>
            <Eye className="h-4 w-4" />
          </Button>
          {isAdmin() && (
            <>
              <Button variant="ghost" size="icon" onClick={() => handleEdit(row)} data-testid={`edit-importacao-${row.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(row.id)} data-testid={`delete-importacao-${row.id}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="importacoes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Importações</h1>
          <p className="text-muted-foreground">Acompanhamento de processos de importação</p>
        </div>
        {isAdmin() && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-importacao-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nova Importação
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingItem ? "Editar Importação" : "Nova Importação"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Número do Processo *</Label>
                    <Input
                      value={formData.numero_processo}
                      onChange={(e) => setFormData({ ...formData, numero_processo: e.target.value })}
                      required
                      data-testid="importacao-processo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fornecedor *</Label>
                    <Select value={formData.fornecedor_id} onValueChange={handleFornecedorChange}>
                      <SelectTrigger data-testid="importacao-fornecedor">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {fornecedoresList.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.razao_social}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Data Pedido</Label>
                    <Input
                      type="date"
                      value={formData.data_pedido}
                      onChange={(e) => setFormData({ ...formData, data_pedido: e.target.value })}
                      data-testid="importacao-data-pedido"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prev. Embarque</Label>
                    <Input
                      type="date"
                      value={formData.data_prevista_embarque}
                      onChange={(e) => setFormData({ ...formData, data_prevista_embarque: e.target.value })}
                      data-testid="importacao-prev-embarque"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prev. Chegada</Label>
                    <Input
                      type="date"
                      value={formData.data_prevista_chegada}
                      onChange={(e) => setFormData({ ...formData, data_prevista_chegada: e.target.value })}
                      data-testid="importacao-prev-chegada"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Frete</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_frete}
                      onChange={(e) => setFormData({ ...formData, valor_frete: parseFloat(e.target.value) || 0 })}
                      data-testid="importacao-frete"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Seguro</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_seguro}
                      onChange={(e) => setFormData({ ...formData, valor_seguro: parseFloat(e.target.value) || 0 })}
                      data-testid="importacao-seguro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Impostos</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_impostos}
                      onChange={(e) => setFormData({ ...formData, valor_impostos: parseFloat(e.target.value) || 0 })}
                      data-testid="importacao-impostos"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Desembaraço</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_desembaraco}
                      onChange={(e) => setFormData({ ...formData, valor_desembaraco: parseFloat(e.target.value) || 0 })}
                      data-testid="importacao-desembaraco"
                    />
                  </div>
                </div>

                {/* Items section */}
                <div className="border rounded-sm p-4 space-y-4">
                  <h3 className="font-semibold">Itens da Importação</h3>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <Select value={newItem.produto_id} onValueChange={handleProdutoChange}>
                        <SelectTrigger data-testid="importacao-item-produto">
                          <SelectValue placeholder="Produto" />
                        </SelectTrigger>
                        <SelectContent>
                          {produtosList.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="number"
                      placeholder="Qtd"
                      value={newItem.quantidade}
                      onChange={(e) => setNewItem({ ...newItem, quantidade: parseInt(e.target.value) || 0 })}
                      data-testid="importacao-item-qtd"
                    />
                    <Button type="button" onClick={addItem} data-testid="importacao-add-item">Adicionar</Button>
                  </div>

                  {formData.itens.length > 0 && (
                    <div className="border rounded-sm overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2">Produto</th>
                            <th className="text-right p-2">Qtd</th>
                            <th className="text-right p-2">Custo Unit.</th>
                            <th className="text-right p-2">Total</th>
                            <th className="p-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.itens.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="p-2">{item.produto_descricao}</td>
                              <td className="p-2 text-right">{item.quantidade}</td>
                              <td className="p-2 text-right">{formatCurrency(item.custo_unitario)}</td>
                              <td className="p-2 text-right">{formatCurrency(item.quantidade * item.custo_unitario)}</td>
                              <td className="p-2">
                                <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-importacao-btn">{editingItem ? "Salvar" : "Criar"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white rounded-sm border p-4 flex items-center gap-4">
        <Ship className="h-8 w-8 text-accent" />
        <div>
          <p className="text-sm text-muted-foreground">Importações em Andamento</p>
          <p className="text-2xl font-heading font-bold">{emAndamento}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-importacoes"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status-importacoes">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="embarcado">Embarcado</SelectItem>
            <SelectItem value="em_transito">Em Trânsito</SelectItem>
            <SelectItem value="desembaracando">Desembaraçando</SelectItem>
            <SelectItem value="concluido">Concluído</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhuma importação encontrada" />

      {/* View Dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importação #{viewItem?.numero_processo}</DialogTitle>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Fornecedor:</span> {viewItem.fornecedor_nome}</div>
                <div><span className="text-muted-foreground">Status:</span> <StatusBadge status={viewItem.status_importacao} /></div>
                <div><span className="text-muted-foreground">Data Pedido:</span> {formatDate(viewItem.data_pedido)}</div>
                <div><span className="text-muted-foreground">Prev. Chegada:</span> {formatDate(viewItem.data_prevista_chegada)}</div>
                <div><span className="text-muted-foreground">Valor Produtos:</span> {formatCurrency(viewItem.valor_total_produtos)}</div>
                <div><span className="text-muted-foreground">Frete:</span> {formatCurrency(viewItem.valor_frete)}</div>
                <div><span className="text-muted-foreground">Seguro:</span> {formatCurrency(viewItem.valor_seguro)}</div>
                <div><span className="text-muted-foreground">Impostos:</span> {formatCurrency(viewItem.valor_impostos)}</div>
              </div>

              {isAdmin() && viewItem.status_importacao !== "concluido" && (
                <div className="space-y-2">
                  <Label>Atualizar Status</Label>
                  <Select onValueChange={(v) => handleUpdateStatus(viewItem.id, v)}>
                    <SelectTrigger data-testid="update-status-importacao">
                      <SelectValue placeholder="Novo status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="embarcado">Embarcado</SelectItem>
                      <SelectItem value="em_transito">Em Trânsito</SelectItem>
                      <SelectItem value="desembaracando">Desembaraçando</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {viewItem.itens?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Itens</h4>
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2">Produto</th>
                          <th className="text-right p-2">Qtd</th>
                          <th className="text-right p-2">Custo Unit.</th>
                          <th className="text-right p-2">Custo Final</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewItem.itens.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2">{item.produto_descricao}</td>
                            <td className="p-2 text-right">{item.quantidade}</td>
                            <td className="p-2 text-right">{formatCurrency(item.custo_unitario)}</td>
                            <td className="p-2 text-right">{formatCurrency(item.custo_final_unitario)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
